"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { AdminLogin } from "./components/admin-login"
import { WelcomeScreen } from "./components/welcome-screen"
import { DashboardHeader } from "./components/dashboard-header"
import { DashboardSidebar } from "./components/dashboard-sidebar"
import { DashboardRightSidebar } from "./components/dashboard-right-sidebar"
import { DashboardContent } from "./components/dashboard-content"
import { LiveMonitorContent } from "./components/live-monitor-content"
import { BotDetectionContent } from "./components/bot-detection-content"
import { TrafficAnalyticsContent } from "./components/traffic-analytics-content"
import { CampaignControlContent } from "./components/campaign-control-content"
import { SecurityConfigContent } from "./components/security-config-content"
import { IPManagementContent } from "./components/ip-management-content"
import { BypassTokensContent } from "./components/bypass-tokens-content"
import { LogViewerContent } from "./components/log-viewer-content"
import { SystemConfigContent } from "./components/system-config-content"
import { TrafficInjectionContent } from "./components/traffic-injection-content"
import DataManagementContent from "./components/data-management-content"
import { apiService } from "./lib/api-service"
import { CONFIG } from "./lib/config"

// Type definitions for better type safety
interface Visitor {
  id: number
  ip: string
  country: string
  device: string
  userAgent: string
  referrer: string
  timestamp: string
}

interface BotActivity {
  id: number
  type: string
  ip: string
  reason: string
  severity: string
  timestamp: string
}

interface SystemStats {
  totalRequests: number
  requestsPerSecond: number
  botBlocked: number
  botPercentage: number
  avgResponseTime: number
  systemHealth: number
  activeVisitors: number
  uniqueVisitors: number
}

interface CampaignStats {
  abTestEnabled: boolean
  campaignA: {
    url: string
    traffic: number
    conversions: number
    bounceRate: number
  }
  campaignB: {
    url: string
    traffic: number
    conversions: number
    bounceRate: number
  }
}

interface DashboardConfig {
  allowThreshold: number
  challengeThreshold: number
  blockThreshold: number
  performanceMode: string
  securityMode: string
  enableRealTimeMonitoring: boolean
  enableIntelligenceEngine: boolean
}

export default function GajahTerbangDashboard() {
  // Auth & UI States
  const [authState, setAuthState] = useState<"login" | "welcome" | "dashboard">("login")
  const [user, setUser] = useState<any>(null)
  const [theme, setTheme] = useState<"dark" | "light">("dark")
  const [currentTime, setCurrentTime] = useState(new Date())
  const [activeMenu, setActiveMenu] = useState("dashboard")

  // System States
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)

  // Data States with proper types
  const [systemStats, setSystemStats] = useState<SystemStats>({
    totalRequests: 0,
    requestsPerSecond: 0,
    botBlocked: 0,
    botPercentage: 0,
    avgResponseTime: 0,
    systemHealth: 0,
    activeVisitors: 0,
    uniqueVisitors: 0,
  })

  const [liveVisitors, setLiveVisitors] = useState<Visitor[]>([])
  const [botActivity, setBotActivity] = useState<BotActivity[]>([])
  const [campaignStats, setCampaignStats] = useState<CampaignStats>({
    abTestEnabled: false,
    campaignA: { url: "", traffic: 0, conversions: 0, bounceRate: 0 },
    campaignB: { url: "", traffic: 0, conversions: 0, bounceRate: 0 },
  })

  // Configuration - sesuai dengan backend
  const [config, setConfig] = useState<DashboardConfig>({
    allowThreshold: 40,
    challengeThreshold: 75,
    blockThreshold: 100,
    performanceMode: "ultra_fast",
    securityMode: "medium",
    enableRealTimeMonitoring: true,
    enableIntelligenceEngine: true,
  })

  // Refs untuk cleanup
  const intervalsRef = useRef<NodeJS.Timeout[]>([])
  const abortControllerRef = useRef<AbortController | null>(null)
  const wsRef = useRef<WebSocket | null>(null)

  // Handle login success
  const handleLoginSuccess = useCallback((userData: any) => {
    setUser(userData)
    setAuthState("welcome")
  }, [])

  // Handle welcome complete dengan proper loading
  const handleWelcomeComplete = useCallback(() => {
    setIsLoading(true)
    // Simulate proper initialization
    setTimeout(() => {
      setAuthState("dashboard")
      setIsLoading(false)
    }, 1500) // Realistic loading time
  }, [])

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  // Time update - optimized
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // Theme persistence
  useEffect(() => {
    const savedTheme = localStorage.getItem("gajah_theme") as "dark" | "light" | null
    if (savedTheme) {
      setTheme(savedTheme)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("gajah_theme", theme)
    document.documentElement.dataset.theme = theme
  }, [theme])

  // WebSocket implementation for real-time updates
  useEffect(() => {
    if (authState !== "dashboard" || !isOnline || !user?.token) return

    const wsUrl = `wss://${CONFIG.WORKER_ENDPOINT}/ws?token=${user.token}`
    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      console.log("WebSocket connected")
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        
        switch (data.channel) {
          case 'traffic':
            setLiveVisitors(prev => [data.payload, ...prev.slice(0, 19)])
            break
          case 'stats':
            setSystemStats(prev => ({ ...prev, ...data.payload }))
            break
          case 'bot-activity':
            setBotActivity(prev => [data.payload, ...prev.slice(0, 49)])
            break
          case 'campaign':
            setCampaignStats(prev => ({ ...prev, ...data.payload }))
            break
        }
        
        setLastUpdate(new Date())
      } catch (error) {
        console.error("WebSocket message parsing error:", error)
      }
    }

    ws.onerror = (error) => {
      console.error("WebSocket error:", error)
      setError("Real-time connection lost. Falling back to polling.")
    }

    ws.onclose = () => {
      console.log("WebSocket disconnected")
    }

    wsRef.current = ws

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [authState, isOnline, user])

  // Data loading functions - sesuai dengan backend API
  const loadInitialData = useCallback(async () => {
    if (!isOnline) return

    setIsLoading(true)
    setError(null)

    // Cancel previous requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    try {
      // Load data sesuai dengan backend endpoints yang benar
      const [statsResponse, visitorsResponse, botDetectionResponse, campaignResponse, configResponse] = await Promise.allSettled([
        apiService.getDashboardStats(), // sesuai dengan /api/stats
        apiService.getLiveTraffic(), // sesuai dengan /api/live-traffic
        apiService.getBotDetection(), // sesuai dengan /api/bot-detection
        apiService.getCampaignStats(), // sesuai dengan /api/campaign-stats
        apiService.getConfig(),
      ])

      // Process stats
      if (statsResponse.status === "fulfilled" && statsResponse.value.success) {
        setSystemStats(prev => ({ ...prev, ...statsResponse.value.data }))
      }

      // Process visitors - perbaiki struktur data
      if (visitorsResponse.status === "fulfilled" && visitorsResponse.value.success) {
        // Backend mengembalikan { feed: [...] }, bukan array langsung
        const visitorData = visitorsResponse.value.data
        setLiveVisitors(visitorData?.feed || visitorData || [])
      }

      // Process bot detection
      if (botDetectionResponse.status === "fulfilled" && botDetectionResponse.value.success) {
        setBotActivity(botDetectionResponse.value.data || [])
      }

      // Process campaign stats
      if (campaignResponse.status === "fulfilled" && campaignResponse.value.success) {
        setCampaignStats(prev => ({ ...prev, ...campaignResponse.value.data }))
      }

      // Process config
      if (configResponse.status === "fulfilled" && configResponse.value.success) {
        setConfig(prev => ({ ...prev, ...configResponse.value.data }))
      }

      setLastUpdate(new Date())
    } catch (error) {
      if (error.name !== "AbortError") {
        setError("Failed to load dashboard data. Some features may be limited.")
        console.error("Dashboard initialization error:", error)
      }
    } finally {
      setIsLoading(false)
    }
  }, [isOnline])

  // Real-time updates - optimized dan sesuai backend
  useEffect(() => {
    if (authState !== "dashboard" || !isOnline) return

    // Skip polling if WebSocket is connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return
    }

    // Clear existing intervals
    intervalsRef.current.forEach(interval => clearInterval(interval))
    intervalsRef.current = []

    // Load initial data
    loadInitialData()

    // Live traffic update
    const trafficInterval = setInterval(async () => {
      try {
        const response = await apiService.getLiveTraffic()
        if (response.success && response.data) {
          // Perbaiki struktur data
          const visitorData = response.data
          setLiveVisitors(visitorData?.feed || visitorData || [])
          setLastUpdate(new Date())
        }
      } catch (error) {
        console.error("Failed to update live traffic:", error)
      }
    }, CONFIG.UPDATE_INTERVALS.LIVE_TRAFFIC)

    // System stats update
    const statsInterval = setInterval(async () => {
      try {
        const response = await apiService.getDashboardStats()
        if (response.success && response.data) {
          setSystemStats(prev => ({ ...prev, ...response.data }))
        }
      } catch (error) {
        console.error("Failed to update stats:", error)
      }
    }, CONFIG.UPDATE_INTERVALS.REAL_TIME_STATS)

    // Bot detection update
    const botInterval = setInterval(async () => {
      try {
        const response = await apiService.getBotDetection()
        if (response.success && response.data) {
          setBotActivity(response.data)
        }
      } catch (error) {
        console.error("Failed to update bot detection:", error)
      }
    }, CONFIG.UPDATE_INTERVALS.INTELLIGENCE)

    // Campaign performance update
    const campaignInterval = setInterval(async () => {
      try {
        const response = await apiService.getCampaignStats()
        if (response.success && response.data) {
          setCampaignStats(prev => ({ ...prev, ...response.data }))
        }
      } catch (error) {
        console.error("Failed to update campaign stats:", error)
      }
    }, CONFIG.UPDATE_INTERVALS.CAMPAIGN_STATS)

    // Config sync - less frequent
    const configInterval = setInterval(async () => {
      try {
        const response = await apiService.getConfig()
        if (response.success && response.data) {
          setConfig(prev => ({ ...prev, ...response.data }))
        }
      } catch (error) {
        console.error("Failed to sync config:", error)
      }
    }, CONFIG.UPDATE_INTERVALS.CONFIG_SYNC)

    // Store intervals for cleanup
    intervalsRef.current = [trafficInterval, statsInterval, botInterval, campaignInterval, configInterval]

    return () => {
      intervalsRef.current.forEach(interval => clearInterval(interval))
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [authState, isOnline, loadInitialData])

  // Theme toggle
  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === "dark" ? "light" : "dark")
  }, [])

  // Security mode change - sesuai dengan backend yang benar
  const handleSecurityModeChange = useCallback(
    async (mode: string) => {
      // Update thresholds sesuai dengan backend logic yang benar
      const modeSettings = {
        easy: { allow: 70, challenge: 90, block: 100 },
        medium: { allow: 40, challenge: 75, block: 100 },
        hard: { allow: 25, challenge: 50, block: 85 },
      }

      const settings = modeSettings[mode as keyof typeof modeSettings]
      if (!settings) return

      const newConfig = {
        ...config,
        securityMode: mode,
        allowThreshold: settings.allow,
        challengeThreshold: settings.challenge,
        blockThreshold: settings.block,
      }

      setConfig(newConfig)

      // Send to API
      try {
        const response = await apiService.updateConfig(newConfig)
        if (!response.success) {
          // Revert on failure
          setConfig(config)
          setError("Failed to update security mode")
        }
      } catch (error) {
        setConfig(config)
        setError("Failed to update security mode")
        console.error("Security mode update error:", error)
      }
    },
    [config]
  )

  // Content renderer
  const renderContent = useCallback(() => {
    const contentProps = {
      systemStats,
      liveVisitors,
      botActivity,
      campaignStats,
      config,
      setConfig,
      isLoading,
      error,
      lastUpdate,
    }

    switch (activeMenu) {
      case "dashboard":
        return <DashboardContent {...contentProps} />
      case "live-monitor":
        return <LiveMonitorContent {...contentProps} />
      case "bot-detection":
        return <BotDetectionContent {...contentProps} />
      case "traffic-analytics":
        return <TrafficAnalyticsContent {...contentProps} />
      case "campaign-control":
        return <CampaignControlContent {...contentProps} setCampaignStats={setCampaignStats} />
      case "security-config":
        return (
          <SecurityConfigContent
            {...contentProps}
            securityMode={config.securityMode}
            handleSecurityModeChange={handleSecurityModeChange}
          />
        )
      case "ip-management":
        return <IPManagementContent {...contentProps} />
      case "bypass-tokens":
        return <BypassTokensContent {...contentProps} />
      case "log-viewer":
        return <LogViewerContent {...contentProps} />
      case "traffic-injection":
        return <TrafficInjectionContent {...contentProps} />
      case "data-management":
        return <DataManagementContent {...contentProps} />
      case "system-config":
        return <SystemConfigContent {...contentProps} />
      default:
        return <DashboardContent {...contentProps} />
    }
  }, [
    activeMenu,
    systemStats,
    liveVisitors,
    botActivity,
    campaignStats,
    config,
    isLoading,
    error,
    lastUpdate,
    handleSecurityModeChange,
  ])

  // Show login screen
  if (authState === "login") {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />
  }

  // Show welcome screen
  if (authState === "welcome") {
    return <WelcomeScreen user={user} onComplete={handleWelcomeComplete} />
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black to-slate-900 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🐘⚡</div>
          <div className="text-white text-xl mb-4">Loading Dashboard...</div>
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    )
  }

  // Show main dashboard
  return (
    <div className={`${theme} min-h-screen bg-gradient-to-br from-black to-slate-900 text-slate-100`}>
      <div className="container mx-auto p-4">
        {/* Offline Banner */}
        {!isOnline && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-300 p-3 rounded-lg mb-4">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
              You are offline. Data may not be current.
            </div>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="bg-yellow-500/20 border border-yellow-500/50 text-yellow-300 p-3 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <span>⚠️ {error}</span>
              <button onClick={() => setError(null)} className="text-yellow-400 hover:text-yellow-200">
                ✕
              </button>
            </div>
          </div>
        )}

        {/* WebSocket Status Indicator */}
        {wsRef.current && wsRef.current.readyState === WebSocket.OPEN && (
          <div className="bg-green-500/20 border border-green-500/50 text-green-300 p-2 rounded-lg mb-4">
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
              Real-time updates active
            </div>
          </div>
        )}

        {/* Header */}
        <DashboardHeader
          theme={theme}
          toggleTheme={toggleTheme}
          systemStats={systemStats}
          user={user}
          isOnline={isOnline}
          lastUpdate={lastUpdate}
        />

        {/* Main content */}
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-12 md:col-span-3 lg:col-span-2">
            <DashboardSidebar
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              systemStats={systemStats}
              isOnline={isOnline}
            />
          </div>

          {/* Main dashboard */}
          <div className="col-span-12 md:col-span-9 lg:col-span-7">
            <div className="grid gap-6">{renderContent()}</div>
          </div>

          {/* Right sidebar */}
          <div className="col-span-12 lg:col-span-3">
            <DashboardRightSidebar
              currentTime={currentTime}
              systemStats={systemStats}
              isOnline={isOnline}
              lastUpdate={lastUpdate}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
